import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Message, ToolApproval, AUTO_TOOLS } from '../../../types';
import { Icons, getAppIcon } from '../../../components/ui/Icons.tsx';
import ActionCard from '../../../components/ui/ActionCard';
import NeuraQuestionAsker from './NeuraQuestionAsker.tsx';
import NeuraLedgerStatus from './NeuraLedgerStatus.tsx';
import { InterventionCard } from '../../shared/components/InterventionCard.tsx';

// ─── Rich Text and Markdown Parser ───
interface RichToken {
  type: 'text' | 'bold' | 'italic' | 'code' | 'url';
  content: string;
}

const parseRichText = (text: string): RichToken[] => {
  if (!text) return [];
  // Regex to match URLs, **bold**, *italic*, and `code`
  const regex = /(https?:\/\/[^\s]+)|\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`/g;
  const tokens: RichToken[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const url = match[1];
    const bold = match[2];
    const italic = match[3];
    const code = match[4];
    const index = match.index;

    if (index > lastIndex) {
      tokens.push({ type: 'text', content: text.substring(lastIndex, index) });
    }

    if (url) {
      tokens.push({ type: 'url', content: url });
    } else if (bold) {
      tokens.push({ type: 'bold', content: bold });
    } else if (italic) {
      tokens.push({ type: 'italic', content: italic });
    } else if (code) {
      tokens.push({ type: 'code', content: code });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'text', content: text.substring(lastIndex) });
  }

  return tokens;
};

const RichText: React.FC<{ text: string }> = ({ text }) => {
  if (!text) return null;
  
  const tokens = parseRichText(text);
  
  return (
    <>
      {tokens.map((token, i) => {
        if (token.type === 'bold') {
          return <strong key={i} className="font-bold">{token.content}</strong>;
        }
        if (token.type === 'italic') {
          return <em key={i} className="italic">{token.content}</em>;
        }
        if (token.type === 'code') {
          return <code key={i} className="px-1.5 py-0.5 font-mono text-xs bg-gray-150 dark:bg-gray-800 text-red-650 dark:text-red-400 rounded">{token.content}</code>;
        }
        if (token.type === 'url') {
          const cleanUrl = token.content.replace(/[.,!;]$/, '');
          const trailing = token.content.length > cleanUrl.length ? token.content.slice(cleanUrl.length) : '';
          
          let AppIcon = null;
          if (cleanUrl.includes('docs.google.com/document')) AppIcon = Icons.GoogleDocs;
          else if (cleanUrl.includes('docs.google.com/spreadsheets')) AppIcon = Icons.GoogleSheets;
          else if (cleanUrl.includes('calendar.google.com')) AppIcon = Icons.GoogleCalendar;
          else if (cleanUrl.includes('notion.so')) AppIcon = Icons.Notion;
          
          return (
            <React.Fragment key={i}>
              <a 
                href={cleanUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline decoration-blue-200 dark:decoration-blue-800 hover:decoration-blue-500 underline-offset-4 inline-flex items-center gap-1.5 font-medium transition-all px-1.5 py-0.5 rounded-md hover:bg-blue-50/50 dark:hover:bg-blue-950/20"
              >
                {AppIcon && <AppIcon className="w-4 h-4" />}
                {cleanUrl}
                <svg className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              {trailing}
            </React.Fragment>
          );
        }
        return token.content;
      })}
    </>
  );
};

// Maintain compatibility for other files/lines referencing Linkify
const Linkify = RichText;

// ─── Streaming Text Effect ───
// Renders text progressively at a natural pace, chunking by words for a fluid feel
const TypingText: React.FC<{ text: string, speed?: number, onComplete?: () => void }> = ({ text, speed = 8, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [index, setIndex] = useState(0);
  const prevTextRef = useRef(text);

  useEffect(() => {
    // Only reset if the text has fundamentally changed (not just appended to)
    if (!text.startsWith(prevTextRef.current) || prevTextRef.current.length === 0) {
      setDisplayedText('');
      setIndex(0);
    }
    prevTextRef.current = text;
  }, [text]);

  useEffect(() => {
    if (index < text.length) {
      // Chunk by word boundaries for more natural feel
      const remaining = text.slice(index);
      const nextSpace = remaining.indexOf(' ');
      const chunkSize = nextSpace > 0 && nextSpace < 8 ? nextSpace + 1 : Math.min(3, remaining.length);
      
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, index + chunkSize));
        setIndex(prev => prev + chunkSize);
      }, speed);
      return () => clearTimeout(timeout);
    } else if (index >= text.length && text.length > 0 && onComplete) {
      onComplete();
    }
  }, [index, text, speed, onComplete]);

  return <RichText text={displayedText} />;
};

// ─── Blinking Cursor ───
const BlinkingCursor: React.FC = () => (
  <span className="inline-block w-[2px] h-[1.1em] bg-[#202124] dark:bg-[#ececec] ml-0.5 align-text-bottom animate-blink" />
);

// ─── Thought Process Toggle ───
// A minimal, non-intrusive toggle for viewing the AI's reasoning
const ThoughtToggle: React.FC<{ text: string }> = ({ text }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!text || text.length < 10) return null;

  return (
    <div className="mt-3 mb-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center gap-1.5 text-[11px] font-medium text-black dark:text-white hover:text-black dark:hover:text-white transition-colors duration-200"
      >
        <svg 
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} 
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400/60" />
          Thought process
        </span>
      </button>
      {isOpen && (
        <div className="mt-2 pl-4 border-l-2 border-violet-100 dark:border-violet-900/50 animate-in fade-in slide-in-from-top-1 duration-200">
          <p className="text-[12px] font-mono leading-relaxed text-black/80 dark:text-white/80 whitespace-pre-wrap bg-gray-50/50 dark:bg-[#171717]/50 p-2 rounded-lg">
            {text}
          </p>
        </div>
      )}
    </div>
  );
};


// ─── Streaming Shimmer ───
// Shows a subtle animated shimmer while waiting for the first token
const StreamingShimmer: React.FC = () => (
  <div className="flex items-center gap-2.5 py-1 animate-in fade-in duration-300">
    <div className="flex items-center gap-1">
      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '0ms' }} />
      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '200ms' }} />
      <div className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: '400ms' }} />
    </div>
  </div>
);

interface ChatMessageProps {
  message: Message;
  onApproveAction?: (messageId: string, approval: ToolApproval, updatedArgs?: any) => void;
  onRejectAction?: (messageId: string, approval: ToolApproval) => void;
  onSendMessage?: (text: string, file?: File, interventionId?: string, interventionAnswer?: any) => void;
  onOpenPricing?: () => void;
  onOpenIntegrations?: () => void;
  onOpenSchedules?: (prefill?: string) => void;
  currentSessionId?: string;
  hideAvatar?: boolean;
  isLast?: boolean;
  isEndOfTurn?: boolean;
  isFinalSuccess?: boolean;
  turnToolApproval?: ToolApproval;
  // Streaming props for real-time text display
  isStreaming?: boolean;
  streamingText?: string;
  streamingThought?: string;
}

// ─── Table Renderer and Parser ───
interface TableBlock {
  type: 'table';
  headers: string[];
  rows: string[][];
}

interface LineBlock {
  type: 'line';
  text: string;
  originalIndex: number;
}

type TextBlock = TableBlock | LineBlock;

const parseBlocks = (lines: string[]): TextBlock[] => {
  const blocks: TextBlock[] = [];
  let i = 0;
  
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    
    // Check if this is the start of a table
    if (trimmed.startsWith('|') && trimmed.endsWith('|') && i + 1 < lines.length) {
      const nextLine = lines[i + 1].trim();
      const isSeparator = nextLine.startsWith('|') && nextLine.endsWith('|') && /^[\s|:-]+$/.test(nextLine);
      
      if (isSeparator) {
        const headers = trimmed
          .split('|')
          .map(s => s.trim())
          .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
          
        const rows: string[][] = [];
        
        // Skip header and separator lines
        i += 2;
        
        // Read data rows
        while (i < lines.length) {
          const rowLine = lines[i].trim();
          if (rowLine.startsWith('|') && rowLine.endsWith('|')) {
            const rowCells = rowLine
              .split('|')
              .map(s => s.trim())
              .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
            rows.push(rowCells);
            i++;
          } else {
            break;
          }
        }
        
        blocks.push({
          type: 'table',
          headers,
          rows
        });
        continue;
      }
    }
    
    blocks.push({
      type: 'line',
      text: line,
      originalIndex: i
    });
    i++;
  }
  
  return blocks;
};

const TableRenderer: React.FC<{ headers: string[], rows: string[][] }> = ({ headers, rows }) => {
  return (
    <div className="overflow-x-auto my-6 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm bg-white dark:bg-[#171717]/30">
      <table className="min-w-full divide-y divide-gray-250 dark:divide-gray-800">
        <thead className="bg-gray-50/80 dark:bg-[#212121]/50 backdrop-blur-sm">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <RichText text={h} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-transparent divide-y divide-gray-150 dark:divide-gray-800/40">
          {rows.map((row, rIdx) => (
            <tr key={rIdx} className="hover:bg-gray-50/50 dark:hover:bg-[#212121]/20 transition-colors">
              {headers.map((_, cIdx) => {
                const cellVal = row[cIdx] || '';
                return (
                  <td key={cIdx} className="px-4 py-3 text-[14px] text-gray-750 dark:text-gray-300 align-top">
                    <RichText text={cellVal} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Formatted Text Renderer ───
// Renders markdown-like content with optional streaming cursor
const FormattedText: React.FC<{ text: string, isStreaming?: boolean }> = ({ text, isStreaming = false }) => {
  const renderLine = (line: string, index: number, isLastLine: boolean) => {
    line = line.trim();
    if (!line) return <div key={index} className="h-2" />;

    const showCursor = isStreaming && isLastLine;

    // 0. Horizontal Rule (--- or ***)
    if (/^[-*_]{3,}$/.test(line)) {
      return <hr key={index} className="my-6 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />;
    }

    // 1. Headers (### Heading)
    if (line.startsWith('###')) {
      return (
        <h3 key={index} className="text-lg font-bold text-[#202124] dark:text-[#ececec] mt-6 mb-3 first:mt-0">
          <RichText text={line.replace(/^#{3,}\s*/, '')} />
          {showCursor && <BlinkingCursor />}
        </h3>
      );
    }
    if (line.startsWith('##') || line.match(/^[A-Z][^a-z]*:$/)) {
      return (
        <h4 key={index} className="text-[17px] font-bold text-[#202124] dark:text-[#ececec] mt-6 mb-3 first:mt-0">
          <RichText text={line.replace(/^#{2}\s*/, '')} />
          {showCursor && <BlinkingCursor />}
        </h4>
      );
    }

    // 1b. Blockquotes (> text)
    if (line.startsWith('>')) {
      const quoteContent = line.replace(/^>\s*/, '');
      return (
        <div key={index} className="pl-4 my-3 border-l-[3px] border-gray-300 dark:border-gray-600">
          <p className="text-[15px] text-[#202124]/80 dark:text-[#ececec]/80 leading-relaxed italic">
            <RichText text={quoteContent} />
            {showCursor && <BlinkingCursor />}
          </p>
        </div>
      );
    }

    // 2. Bullet Lists (- Item)
    if (line.match(/^[-*•]\s/)) {
        const content = line.replace(/^[-*•]\s/, '');
        return (
            <div key={index} className="flex gap-3 mb-2 ml-1">
              <span className="text-black dark:text-white mt-1.5 flex-shrink-0">•</span>
              <p className="text-[15px] text-[#202124] dark:text-[#ececec] leading-relaxed">
                <RichText text={content} />
                {showCursor && <BlinkingCursor />}
              </p>
            </div>
        );
    }

    // 3. Numbered Lists (1. Item)
    const numMatch = line.match(/^(\d+)\.\s(.*)/);
    if (numMatch) {
        const content = numMatch[2];
        return (
            <div key={index} className="flex gap-3 mb-3 ml-1">
              <span className="font-bold text-[#202124] dark:text-[#ececec] mt-0.5 flex-shrink-0 text-[14px] w-4">{numMatch[1]}.</span>
              <div className="text-[15px] text-[#202124] dark:text-[#ececec] leading-relaxed">
                <RichText text={content} />
                {showCursor && <BlinkingCursor />}
              </div>
            </div>
        );
    }

    // 4. Regular Paragraph — RichText handles all inline bold/italic/code/url
    return (
      <p key={index} className="text-[15px] text-[#202124] dark:text-[#ececec] leading-relaxed mb-4">
        <RichText text={line} />
        {showCursor && <BlinkingCursor />}
      </p>
    );
  };

  const lines = text.split('\n');
  const blocks = parseBlocks(lines);
  
  return (
    <div className="w-full">
      {blocks.map((block, idx) => {
        if (block.type === 'table') {
          return <TableRenderer key={idx} headers={block.headers} rows={block.rows} />;
        }
        return renderLine(block.text, idx, block.originalIndex === lines.length - 1);
      })}
    </div>
  );
};

const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message, onApproveAction, onRejectAction, onSendMessage, onOpenPricing, onOpenIntegrations, 
  onOpenSchedules, currentSessionId,
  hideAvatar, isLast, isEndOfTurn, isFinalSuccess,
  isStreaming, streamingText, streamingThought,
  turnToolApproval
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isShared, setIsShared] = useState(false);

  const isUser = message.type === 'user';
  const isSystem = message.type === 'system';
  const isAI = !isUser && !isSystem;

  const showOptionsStrip = useMemo(() => {
    if (isStreaming || isUser || !isEndOfTurn) return false;
    
    const effectiveToolApproval = message.toolApproval || turnToolApproval;
    if (effectiveToolApproval) {
      return effectiveToolApproval.status === 'executed';
    }
    
    return true;
  }, [isStreaming, isUser, isEndOfTurn, message.toolApproval, turnToolApproval]);

  const handleCopy = () => {
    const textToCopy = message.summary || message.content;
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleShare = () => {
    if (!currentSessionId) return;
    const shareUrl = `${window.location.origin}/chat/${currentSessionId}`;
    navigator.clipboard.writeText(shareUrl);
    setIsShared(true);
    setTimeout(() => setIsShared(false), 2000);
  };

  const handleAddToSchedule = () => {
    if (!onOpenSchedules) return;
    let prefill = message.summary || message.content;
    const effectiveToolApproval = message.toolApproval || turnToolApproval;
    if (effectiveToolApproval && effectiveToolApproval.status === 'executed') {
      const toolName = effectiveToolApproval.toolName.replace(/_/g, ' ');
      const argsStr = Object.entries(effectiveToolApproval.args || {}).map(([k, v]) => `${k}="${v}"`).join(' ');
      prefill = `Schedule ${toolName} with ${argsStr}`;
    }
    // Clean up generic prefixes and markdown if present
    prefill = prefill
      .replace(/\*\*/g, '')
      .replace(/^(I have|Successfully|Executed|Actually) /i, '')
      .trim();
    onOpenSchedules(prefill);
  };

  const [isActionProcessing, setIsActionProcessing] = useState(false);

  // Helper to handle button clicks with loading state
  const handleAction = async (type: 'approve' | 'reject') => {
      if (!message.toolApproval || isActionProcessing) return;
      
      setIsActionProcessing(true);
      try {
          if (type === 'approve' && onApproveAction) {
              await onApproveAction(message.id, message.toolApproval);
          } else if (type === 'reject' && onRejectAction) {
              await onRejectAction(message.id, message.toolApproval);
          }
      } finally {
          setIsActionProcessing(false);
      }
  };

  // Extract thought content from toolApproval description for the toggle
  const thoughtText = useMemo(() => {
    if (message.toolApproval?.description && !message.toolApproval.description.startsWith("Execute ")) {
      return message.toolApproval.description;
    }
    return '';
  }, [message.toolApproval?.description]);

  // Render content with interleaved paragraphs and action logs
  const renderInterleavedContent = () => {
    const content = message.content;
    
    // 2. AI Message Processing
    if (isAI) {
        let resultData = null;
        let contentText = content;
        
        // Find JSON objects or arrays in the content
        const startIndex = Math.min(
            contentText.indexOf('[') === -1 ? Infinity : contentText.indexOf('['),
            contentText.indexOf('{') === -1 ? Infinity : contentText.indexOf('{')
        );
        const endIndex = Math.max(
            contentText.lastIndexOf(']'),
            contentText.lastIndexOf('}')
        );

        if (startIndex !== Infinity && endIndex !== -1 && endIndex > startIndex) {
            const potentialJson = contentText.substring(startIndex, endIndex + 1);
            try {
                const data = JSON.parse(potentialJson);
                const isLikelyData = (Array.isArray(data) && data.length > 0) || (typeof data === 'object' && data !== null && (data.tool || data.actions || data.thought));
                
                if (isLikelyData) {
                    resultData = data;
                    contentText = contentText.substring(0, startIndex) + contentText.substring(endIndex + 1);
                }
            } catch (e) {}
        }
        
        const cleanText = contentText.replace(/```json/g, '').replace(/```/g, '').trim();

        return (
            <div className="space-y-1">
                <div className="py-1">
                    <FormattedText text={cleanText} isStreaming={isStreaming} />
                </div>
                
                {/* Thought process toggle — minimal, below the response */}
                {thoughtText && !isStreaming && (
                  <ThoughtToggle text={thoughtText} />
                )}
                
                {resultData && !message.toolApproval && !content.startsWith('Execution Result:') && (
                    <div className="mt-4">
                        <ActionCard 
                            resultData={resultData}
                            resultTool={resultData.tool || resultData.tool_name || 'tool'}
                            onSendMessage={onSendMessage}
                            onOpenPricing={onOpenPricing}
                            onOpenIntegrations={onOpenIntegrations}
                        />
                    </div>
                )}
            </div>
        );
    }

    // 3. User Message Processing
    if (isUser) {
        const fileMarker = '[ATTACHED FILE:';
        if (content.includes(fileMarker)) {
            const cleanContent = content.split(fileMarker)[0].trim();
            return cleanContent || (message.attachment ? '' : content);
        }
        return content;
    }

    return content;
  };

  // Status messages (pulse/centered) are only for specific transient states
  const isStatusMessage = message.type === 'system' && (message.content === 'Analyzing request...' || message.content.includes('Thinking') || message.content.includes('Executing'));
  
  // Hide system messages that are just execution results for the LLM
  // We keep 'rejected' status system messages as they provide useful feedback
  const isExecutionResult = message.type === 'system' && (
    message.content.startsWith('Execution Result:') || 
    (message.toolApproval && (message.toolApproval.status === 'executed' || message.toolApproval.status === 'failed'))
  );
  
  // Hide avatar for results to keep things grouped
  const actualHideAvatar = hideAvatar || isExecutionResult;

  // Totally hide execution results from the UI as they are redundant now (results are in cards)
  if (isExecutionResult) return null;

  // Replace the old ThinkingBox status message with the subtle shimmer
  if (isStatusMessage) {
    return (
        <div className={`flex w-full ${actualHideAvatar ? 'mb-2' : 'mb-8'} justify-start`}>
            <div className={`max-w-[90%] md:max-w-[85%] flex gap-4 flex-row ${actualHideAvatar ? 'md:pl-12' : ''}`}>
                {!actualHideAvatar && (
                    <div className="flex-shrink-0 mt-1 hidden md:block">
                        <div className="w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center bg-white dark:bg-[#212121] border border-gray-100 dark:border-gray-800 shadow-sm">
                            <Icons.Logo className="w-5 h-5" />
                        </div>
                    </div>
                )}
                <div className="flex flex-col items-start w-full py-2">
                    <StreamingShimmer />
                </div>
            </div>
        </div>
    );
  }

  return (
    <div className={`flex w-full ${isSystem ? 'my-1' : (actualHideAvatar ? 'mt-[-8px] mb-1' : 'mb-8')} ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[90%] md:max-w-[85%] flex gap-4 ${isUser ? 'flex-row-reverse' : 'flex-row w-full'} ${actualHideAvatar ? 'md:pl-12' : ''}`}>
        {/* Avatar Area (Hidden for User and Execution Results) */}
        {!isUser && !actualHideAvatar && (
          <div className="flex-shrink-0 mt-1 hidden md:block">
             <div className="w-8 h-8 rounded-xl overflow-hidden flex items-center justify-center bg-white dark:bg-[#212121] border border-gray-100 dark:border-gray-800 shadow-sm">
                <Icons.Logo className="w-5 h-5" />
             </div>
          </div>
        )}

        {/* Message Content */}
        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} w-full`}>
            {/* Main Text Bubble */}
            <div className={`
                relative group/message text-[15px] break-words w-full
                ${isUser 
                    ? 'bg-[#F1F3F4] dark:bg-[#212121] text-[#202124] dark:text-[#ececec] rounded-2xl px-5 py-3.5 max-w-fit self-end' 
                    : isSystem
                    ? 'font-mono text-xs text-black dark:text-gray-400 px-5'
                    : 'text-[#202124] dark:text-[#ececec] bg-white dark:bg-transparent w-full max-w-[720px] md:min-w-[720px] mx-auto py-2' // AI response - plain text
                }
            `}>
                {/* Inline Copy Button - Only for User messages as requested */}
                {isUser && (
                    <button
                        onClick={handleCopy}
                        className={`
                            absolute top-1/2 -translate-y-1/2 p-2 rounded-lg 
                            bg-white/80 dark:bg-[#171717]/80 backdrop-blur-sm border border-gray-100 dark:border-gray-800 shadow-sm
                            text-black dark:text-white hover:text-black dark:hover:text-white transition-all duration-200
                            opacity-0 group-hover/message:opacity-100
                            -left-12
                            hidden md:flex items-center justify-center
                        `}
                        title="Copy message"
                    >
                        {isCopied ? <Icons.Check className="w-3.5 h-3.5 text-black dark:text-white" /> : <Icons.Copy className="w-3.5 h-3.5" />}
                    </button>
                )}
                {message.attachment && (
                    <div className="mb-4 flex items-center gap-3 p-3 rounded-2xl border bg-gray-50 dark:bg-[#050505] border-gray-100 dark:border-gray-800 max-w-sm">
                        <div className="w-10 h-10 rounded-xl bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-850 flex items-center justify-center shadow-sm">
                            <Icons.Paperclip className="w-5 h-5 text-black dark:text-white" />
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold truncate">{message.attachment.name}</span>
                            <span className="text-[11px] text-black dark:text-gray-400">
                                {(message.attachment.size / 1024).toFixed(1)} KB
                            </span>
                        </div>
                    </div>
                )}

                {renderInterleavedContent()}

                {/* --- EXECUTION LEDGER STATUS --- */}
                {message.ledger && (
                    <div className="w-full mt-4">
                        <NeuraLedgerStatus ledger={message.ledger} />
                    </div>
                )}
                
                {/* --- INTERVENTION CARD --- */}
                {message.intervention && (
                    <div className="w-full mt-4">
                        <InterventionCard 
                            intervention={message.intervention} 
                            disabled={message.intervention.status === 'resolved'}
                            onSubmit={(answer) => {
                                if (onSendMessage) {
                                    onSendMessage('', undefined, message.intervention!.intervention_id, answer);
                                }
                            }}
                        />
                    </div>
                )}

                {/* --- ACTION CARD / QUESTION ASKER --- */}
                {message.toolApproval && !isSystem && (
                    <div className="w-full mt-4">
                        {message.toolApproval.toolName === 'ask_user_clarification' ? (
                            <NeuraQuestionAsker 
                                question={message.toolApproval.args?.question || "Please clarify:"}
                                options={message.toolApproval.args?.options}
                                system={message.toolApproval.args?.system}
                                onSubmit={(answer) => {
                                    if (onApproveAction) {
                                        onApproveAction(message.id, message.toolApproval!, { answer });
                                    }
                                }}
                            />
                        ) : (
                            <ActionCard 
                                toolApproval={message.toolApproval}
                                onApprove={(approval, args) => onApproveAction?.(message.id, approval, args)} 
                                onReject={() => onRejectAction?.(message.id, message.toolApproval!)}
                                onSendMessage={onSendMessage}
                                onOpenPricing={onOpenPricing}
                                onOpenIntegrations={onOpenIntegrations}
                            />
                        )}
                    </div>
                )}

                {/* Final Summary Text */}
                {message.summary && (message.summary.trim() !== message.content.trim() || !message.content.trim()) && (
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 animate-in fade-in slide-in-from-top-2 duration-500">
                        <div className="flex items-start gap-3">
                            <div className="mt-1 flex-shrink-0">
                                <Icons.Check className="w-5 h-5 text-black dark:text-white bg-gray-100 dark:bg-[#212121] rounded-full p-1" />
                            </div>
                            <div className="text-[15px] leading-relaxed text-[#202124] dark:text-[#ececec]">
                                <TypingText text={message.summary} speed={8} />
                            </div>
                        </div>
                    </div>
                )}



                {/* Task Actions: Copy, Share, Add to Schedule */}
                {showOptionsStrip && (
                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 animate-in fade-in slide-in-from-bottom-1 duration-300">
                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-[#171717]/50 hover:bg-gray-100 dark:hover:bg-[#212121]/80 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white shadow-sm transition-all duration-200"
                      title="Copy response"
                    >
                      {isCopied ? (
                        <>
                          <Icons.Check className="w-3.5 h-3.5 text-black dark:text-white" />
                          <span className="text-black dark:text-white">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Icons.Copy className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleShare}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-[#171717]/50 hover:bg-gray-100 dark:hover:bg-[#212121]/80 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white shadow-sm transition-all duration-200"
                      title="Copy share link"
                    >
                      {isShared ? (
                        <>
                          <Icons.Check className="w-3.5 h-3.5 text-black dark:text-white" />
                          <span className="text-black dark:text-white">Link Copied!</span>
                        </>
                      ) : (
                        <>
                          <Icons.Share className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                          <span>Share</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleAddToSchedule}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-[#171717]/50 hover:bg-gray-100 dark:hover:bg-[#212121]/80 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white shadow-sm transition-all duration-200"
                    >
                      <Icons.CalendarClock className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                      <span>Add to Schedule</span>
                    </button>
                  </div>
                )}
            </div>
            

        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
